import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Pack-aware effective unit cost. Returns the cost the manager should
// pay per the *component's* unit, given the item's pack metadata.
//
//   item.unit=case, cost_per_unit=$26.99, pack_size=24, pack_unit=each
//   component.unit='each'  → returns 26.99 / 24 = 1.124
//   component.unit='case'  → returns 26.99
//   component.unit=null    → returns 26.99 (treat as item.unit)
//
// Lower-cased unit comparison so 'Each' / 'each' / 'EACH' all match.
// Falls back to cost_per_unit when no pack info or unit doesn't match.
//
// Null component-unit handling: if component.unit is missing, we
// treat the component as referring to the SMALLEST meaningful unit —
// pack_unit if it's set, otherwise item.unit. Without this fallback,
// a recipe row with unit=NULL on a packed item like
// "D'GARI case (24 packets) at $26.99" would charge the full case
// cost per quantity (3 packets → $80.97 instead of $3.37). The AI
// recipe builder always sets a unit, but manual SQL/API edits or
// future inline editing in the app could leave it null. This guard
// turns the silent overcharge into the correct inner-unit cost.
export function effectiveUnitCost(item, componentUnit) {
  if (!item) return 0
  const base = Number(item.cost_per_unit || 0)
  const packSize = Number(item.pack_size || 1)
  if (!(packSize > 1) || !item.pack_unit) return base
  const cu = (componentUnit || item.pack_unit || item.unit || '')
              .toLowerCase().trim()
  const pu = (item.pack_unit || '').toLowerCase().trim()
  if (cu && cu === pu) return base / packSize
  return base
}

// Drives the Recipes section in Settings.
//
// Pulls all recipes + their components for the user's restaurant
// (RLS auto-scopes), joined to items so we can show the
// component name and current cost without a second query.
//
// Mutations:
//   - createRecipe({ name, yield_servings, target_food_cost_pct, components })
//     creates the parent recipe + all components in two inserts.
//     Used by the AI build flow's Accept button.
//   - deleteRecipe(id) cascades to components via the FK.
//
// Future additions (deferred): updateRecipe, addComponent,
// removeComponent inline in the row. For Tier 1 the manager
// re-creates from the AI flow if they want to change a recipe.
export function useRecipesData() {
  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Single query with embedded select for components + their item joins.
      // RLS scopes the user to their own restaurant automatically.
      // pack_size + pack_unit pulled in so we can compute cost in the
      // inner unit when a component's unit matches pack_unit (per-each
      // costing on case-tracked items, etc).
      const { data, error: fetchErr } = await supabase
        .from('recipes')
        .select(`
          id, name, yield_servings, target_food_cost_pct, item_id, notes,
          created_at, updated_at,
          components:recipe_components(
            id, item_id, quantity, unit, notes,
            item:item_id(id, name, unit, cost_per_unit, sale_price, pack_size, pack_unit)
          )
        `)
        .order('name', { ascending: true })

      if (fetchErr) throw fetchErr
      setRecipes(data ?? [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useRecipesData]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Compute live cost per recipe from current cost_per_unit on each
  // component's item. This reflects today's costs even if the recipe
  // was created when prices were different — manager always sees
  // up-to-date COGS.
  //
  // Pack-aware: when a component's unit matches the item's pack_unit
  // (e.g. recipe needs "1 each" of an item bought "case" of 24 each),
  // the effective per-component cost is cost_per_unit / pack_size.
  // Falls back to raw cost_per_unit when pack_size = 1 or pack_unit
  // doesn't match.
  const recipesWithCost = useMemo(() => {
    return (recipes || []).map((r) => {
      let total_cogs = 0
      let cost_known = true
      for (const c of (r.components || [])) {
        const item = c.item
        const baseCost = Number(item?.cost_per_unit || 0)
        if (baseCost <= 0) cost_known = false
        const effCost = effectiveUnitCost(item, c.unit)
        total_cogs += effCost * Number(c.quantity || 0)
      }
      const yieldN = Number(r.yield_servings || 1)
      const cost_per_serving = yieldN > 0 ? total_cogs / yieldN : 0
      const target_pct = Number(r.target_food_cost_pct || 0)
      const target_price = target_pct > 0 ? cost_per_serving / (target_pct / 100) : 0
      return {
        ...r,
        total_cogs,
        cost_per_serving,
        target_price,
        cost_known,
      }
    })
  }, [recipes])

  // ---------- Create ---------------------------------------
  // Two-step insert: recipe parent first, then components in bulk.
  // Wrapping in a try so a partial failure leaves the orphan recipe
  // for the manager to delete or retry against.
  const createRecipe = useCallback(async ({
    name,
    yield_servings,
    target_food_cost_pct,
    notes,
    components,        // [{ item_id, quantity, unit, notes }]
    item_id,           // optional explicit link to a menu item
  }) => {
    if (!name || !Array.isArray(components)) {
      throw new Error('Recipe needs a name and a components array')
    }

    // Auto-link to a menu item by normalized name match if the caller
    // didn't provide one. Variance reporting uses recipe.item_id to
    // join against item_sales — without a link, the recipe is
    // invisible to the variance report.
    //
    // Normalization is done client-side because Postgres ilike is
    // case-insensitive but NOT apostrophe-aware: "D'Gari" vs "D'GARI"
    // (curly vs straight apostrophe) would silently fail an ilike
    // match. We collapse all common apostrophe / quote variants to a
    // single ASCII form on both sides before comparing.
    let resolvedItemId = item_id || null
    if (!resolvedItemId) {
      const norm = (s) => (s || '')
        .replace(/[\u2018\u2019\u201A\u201B\u02BC\u2032]/g, "'")  // curly → straight apostrophe
        .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')        // curly → straight quote
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
      const target = norm(name)
      const { data: allItems } = await supabase
        .from('items')
        .select('id, name')
      const match = (allItems ?? []).find((it) => norm(it.name) === target)
      if (match) resolvedItemId = match.id
    }

    const { data: created, error: insErr } = await supabase
      .from('recipes')
      .insert({
        name:                 name.trim(),
        yield_servings:       Number(yield_servings) || 1,
        target_food_cost_pct: target_food_cost_pct != null ? Number(target_food_cost_pct) : null,
        notes:                notes?.trim() || null,
        item_id:              resolvedItemId,
      })
      .select()
      .single()
    if (insErr) throw insErr

    if (components.length > 0) {
      const componentInserts = components
        .filter((c) => c.item_id && Number(c.quantity) > 0)
        .map((c) => ({
          recipe_id: created.id,
          item_id:   c.item_id,
          quantity:  Number(c.quantity),
          unit:      c.unit || null,
          notes:     c.notes || null,
        }))
      if (componentInserts.length > 0) {
        const { error: cErr } = await supabase
          .from('recipe_components')
          .insert(componentInserts)
        if (cErr) throw cErr
      }
    }

    await fetchAll()
    return created
  }, [fetchAll])

  // ---------- Delete --------------------------------------
  const deleteRecipe = useCallback(async (id) => {
    const { error: delErr } = await supabase
      .from('recipes')
      .delete()
      .eq('id', id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  // ---------- Update item_id link ------------------------
  // Manager-driven explicit link from the Recipes list. Returns
  // { data, error } instead of throwing so the calling modal can
  // distinguish FK violations (item deleted between render and save)
  // from network errors and surface the right message.
  const updateRecipeItemId = useCallback(async (recipeId, itemId) => {
    const { data, error: updErr } = await supabase
      .from('recipes')
      .update({ item_id: itemId })
      .eq('id', recipeId)
      .select('id, item_id')
      .single()
    if (!updErr) await fetchAll()
    return { data, error: updErr }
  }, [fetchAll])

  return {
    recipes: recipesWithCost,
    loading,
    error,
    refresh: fetchAll,
    createRecipe,
    deleteRecipe,
    updateRecipeItemId,
  }
}
